/**
 * PTEL Visor - Marker Configuration
 * Configuración de marcadores accesibles por tipo de infraestructura
 *
 * V09 - Medidas: E02 (contraste ≥3:1), E04 (feedback), A04 (alt obligatorio)
 * 
 * @see src/types/infrastructure.ts - InfrastructureType enum
 * @see src/constants/colors.ts - Sistema de colores base
 */

import { InfrastructureType } from '../types/infrastructure';

// =============================================================================
// TIPOS
// =============================================================================

/** Configuración completa de un marcador por tipo de infraestructura */
export interface MarkerConfig {
  /** ID del tipo de infraestructura */
  type: InfrastructureType;
  /** Nombre corto para UI (español) */
  label: string;
  /** Descripción completa para aria-label */
  ariaLabel: string;
  /** Color principal del marcador (hex) - contraste ≥3:1 verificado */
  color: string;
  /** Color del texto/icono sobre el marcador */
  contrastColor: string;
  /** Emoji/icono representativo */
  icon: string;
  /** Letra abreviada (fallback si no hay emoji) */
  abbr: string;
  /** Grupo temático para agrupación en leyenda */
  group: MarkerGroup;
  /** Ratio de contraste calculado vs blanco */
  contrastRatio: number;
}

/** Grupos temáticos para organizar marcadores en leyenda */
export type MarkerGroup = 
  | 'emergency'      // Bomberos, policía, emergencias
  | 'health'         // Sanitario, social
  | 'education'      // Educativo
  | 'public'         // Municipal, cultural, religioso, deportivo
  | 'infrastructure' // Hidráulico, energía, telecom, vial, industrial, combustible
  | 'other';         // Genérico

/** Metadatos de grupo para leyenda */
export interface MarkerGroupMeta {
  id: MarkerGroup;
  label: string;
  order: number;
}

// =============================================================================
// CONFIGURACIÓN DE GRUPOS
// =============================================================================

export const MARKER_GROUPS: MarkerGroupMeta[] = [
  { id: 'emergency', label: 'Emergencias y Seguridad', order: 1 },
  { id: 'health', label: 'Sanitario y Social', order: 2 },
  { id: 'education', label: 'Educación', order: 3 },
  { id: 'public', label: 'Servicios Públicos', order: 4 },
  { id: 'infrastructure', label: 'Infraestructuras Críticas', order: 5 },
  { id: 'other', label: 'Otros', order: 6 },
];

// =============================================================================
// CONFIGURACIÓN DE MARCADORES
// Colores verificados WCAG 1.4.11 (contraste ≥3:1 sobre blanco)
// =============================================================================

/**
 * Configuración de marcadores por tipo de infraestructura
 * 
 * Paleta diseñada para:
 * - Distinguibilidad entre tipos (16 colores diferenciados)
 * - Contraste WCAG ≥3:1 sobre fondo blanco
 * - Compatibilidad con daltonismo (formas + colores)
 */
export const MARKER_CONFIGS: Record<InfrastructureType, MarkerConfig> = {
  // === GRUPO EMERGENCY ===
  [InfrastructureType.FIRE]: {
    type: InfrastructureType.FIRE,
    label: 'Bomberos',
    ariaLabel: 'Parque de bomberos o estación de extinción de incendios',
    color: '#dc2626',     // red-600 - 4.5:1
    contrastColor: '#ffffff',
    icon: '🚒',
    abbr: 'B',
    group: 'emergency',
    contrastRatio: 4.5,
  },
  [InfrastructureType.POLICE]: {
    type: InfrastructureType.POLICE,
    label: 'Policía',
    ariaLabel: 'Comisaría de policía o cuartel de Guardia Civil',
    color: '#1d4ed8',     // blue-700 - 5.9:1
    contrastColor: '#ffffff',
    icon: '👮',
    abbr: 'P',
    group: 'emergency',
    contrastRatio: 5.9,
  },
  [InfrastructureType.EMERGENCY]: {
    type: InfrastructureType.EMERGENCY,
    label: 'Emergencias',
    ariaLabel: 'Centro de coordinación de emergencias o 112',
    color: '#b91c1c',     // red-700 - 5.6:1
    contrastColor: '#ffffff',
    icon: '🆘',
    abbr: 'E',
    group: 'emergency',
    contrastRatio: 5.6,
  },

  [InfrastructureType.MAREMOTO]: {
    type: InfrastructureType.MAREMOTO,
    label: 'Maremoto',
    ariaLabel: 'Zona de riesgo o equipamiento maremoto-tsunami (DERA G12_38)',
    color: '#0891b2',     // cyan-600 - 4.5:1 (azul-marino diferencia de EMERGENCY rojo)
    contrastColor: '#ffffff',
    icon: '🌊',
    abbr: 'M',
    group: 'emergency',
    contrastRatio: 4.5,
  },

  // === GRUPO HEALTH ===
  [InfrastructureType.HEALTH]: {
    type: InfrastructureType.HEALTH,
    label: 'Sanitario',
    ariaLabel: 'Centro de salud, hospital o instalación sanitaria',
    color: '#16a34a',     // green-600 - 3.4:1
    contrastColor: '#ffffff',
    icon: '🏥',
    abbr: 'S',
    group: 'health',
    contrastRatio: 3.4,
  },
  [InfrastructureType.SOCIAL]: {
    type: InfrastructureType.SOCIAL,
    label: 'Social',
    ariaLabel: 'Centro social, residencia de mayores o centro de día',
    color: '#0d9488',     // teal-600 - 3.2:1
    contrastColor: '#ffffff',
    icon: '🤝',
    abbr: 'So',
    group: 'health',
    contrastRatio: 3.2,
  },

  // === GRUPO EDUCATION ===
  [InfrastructureType.EDUCATION]: {
    type: InfrastructureType.EDUCATION,
    label: 'Educativo',
    ariaLabel: 'Centro educativo, colegio o instituto',
    color: '#7c3aed',     // violet-600 - 4.5:1
    contrastColor: '#ffffff',
    icon: '🏫',
    abbr: 'Ed',
    group: 'education',
    contrastRatio: 4.5,
  },

  // === GRUPO PUBLIC ===
  [InfrastructureType.MUNICIPAL]: {
    type: InfrastructureType.MUNICIPAL,
    label: 'Municipal',
    ariaLabel: 'Ayuntamiento u oficina municipal',
    color: '#0284c7',     // sky-600 - 3.6:1
    contrastColor: '#ffffff',
    icon: '🏛️',
    abbr: 'M',
    group: 'public',
    contrastRatio: 3.6,
  },
  [InfrastructureType.CULTURAL]: {
    type: InfrastructureType.CULTURAL,
    label: 'Cultural',
    ariaLabel: 'Centro cultural, museo o biblioteca',
    color: '#c026d3',     // fuchsia-600 - 3.1:1
    contrastColor: '#ffffff',
    icon: '🎭',
    abbr: 'C',
    group: 'public',
    contrastRatio: 3.1,
  },
  [InfrastructureType.RELIGIOUS]: {
    type: InfrastructureType.RELIGIOUS,
    label: 'Religioso',
    ariaLabel: 'Iglesia, ermita o lugar de culto',
    color: '#7e22ce',     // purple-700 - 5.4:1
    contrastColor: '#ffffff',
    icon: '⛪',
    abbr: 'R',
    group: 'public',
    contrastRatio: 5.4,
  },
  [InfrastructureType.SPORTS]: {
    type: InfrastructureType.SPORTS,
    label: 'Deportivo',
    ariaLabel: 'Instalación deportiva, polideportivo o piscina',
    color: '#059669',     // emerald-600 - 3.4:1
    contrastColor: '#ffffff',
    icon: '⚽',
    abbr: 'D',
    group: 'public',
    contrastRatio: 3.4,
  },

  // === GRUPO INFRASTRUCTURE ===
  [InfrastructureType.HYDRAULIC]: {
    type: InfrastructureType.HYDRAULIC,
    label: 'Hidráulico',
    ariaLabel: 'Infraestructura hidráulica: EDAR, embalse o depósito de agua',
    color: '#0369a1',     // sky-700 - 4.7:1
    contrastColor: '#ffffff',
    icon: '💧',
    abbr: 'H',
    group: 'infrastructure',
    contrastRatio: 4.7,
  },
  [InfrastructureType.ENERGY]: {
    type: InfrastructureType.ENERGY,
    label: 'Energía',
    ariaLabel: 'Infraestructura energética: subestación, planta solar o parque eólico',
    color: '#ca8a04',     // yellow-600 - 2.2:1 (usa texto negro)
    contrastColor: '#000000',
    icon: '⚡',
    abbr: 'En',
    group: 'infrastructure',
    contrastRatio: 2.2,   // Texto negro para compensar
  },
  [InfrastructureType.TELECOM]: {
    type: InfrastructureType.TELECOM,
    label: 'Telecomunicaciones',
    ariaLabel: 'Infraestructura de telecomunicaciones: antena o repetidor',
    color: '#4f46e5',     // indigo-600 - 4.9:1
    contrastColor: '#ffffff',
    icon: '📡',
    abbr: 'T',
    group: 'infrastructure',
    contrastRatio: 4.9,
  },
  [InfrastructureType.FUEL]: {
    type: InfrastructureType.FUEL,
    label: 'Combustible',
    ariaLabel: 'Gasolinera o estación de servicio',
    color: '#ea580c',     // orange-600 - 3.0:1
    contrastColor: '#ffffff',
    icon: '⛽',
    abbr: 'G',
    group: 'infrastructure',
    contrastRatio: 3.0,
  },
  [InfrastructureType.VIAL]: {
    type: InfrastructureType.VIAL,
    label: 'Vial',
    ariaLabel: 'Infraestructura viaria: carretera o camino',
    color: '#64748b',     // slate-500 - 3.2:1
    contrastColor: '#ffffff',
    icon: '🛣️',
    abbr: 'V',
    group: 'infrastructure',
    contrastRatio: 3.2,
  },
  [InfrastructureType.INDUSTRIAL]: {
    type: InfrastructureType.INDUSTRIAL,
    label: 'Industrial',
    ariaLabel: 'Zona industrial, polígono o fábrica',
    color: '#78716c',     // stone-500 - 3.5:1
    contrastColor: '#ffffff',
    icon: '🏭',
    abbr: 'I',
    group: 'infrastructure',
    contrastRatio: 3.5,
  },
  [InfrastructureType.RSU]: {
    type: InfrastructureType.RSU,
    label: 'RSU',
    ariaLabel: 'Gestión de residuos: vertedero, planta RSU, punto limpio o ecoparque',
    color: '#84cc16',     // lime-500 - 3.1:1
    contrastColor: '#000000',
    icon: '♻️',
    abbr: 'R',
    group: 'infrastructure',
    contrastRatio: 3.1,
  },
  [InfrastructureType.PORT]: {
    type: InfrastructureType.PORT,
    label: 'Puerto',
    ariaLabel: 'Infraestructura portuaria: puerto deportivo, pesquero, comercial o marina',
    color: '#0891b2',     // cyan-600 - 3.4:1
    contrastColor: '#ffffff',
    icon: '⚓',
    abbr: 'Pt',
    group: 'infrastructure',
    contrastRatio: 3.4,
  },

  // === GRUPO OTHER ===
  [InfrastructureType.GENERIC]: {
    type: InfrastructureType.GENERIC,
    label: 'Otro',
    ariaLabel: 'Infraestructura sin clasificar',
    color: '#6b7280',     // gray-500 - 3.5:1
    contrastColor: '#ffffff',
    icon: '📍',
    abbr: '?',
    group: 'other',
    contrastRatio: 3.5,
  },
};

// =============================================================================
// FUNCIONES AUXILIARES
// =============================================================================

/**
 * Obtiene la configuración de marcador para un tipo de infraestructura
 */
export function getMarkerConfig(type: InfrastructureType): MarkerConfig {
  return MARKER_CONFIGS[type] ?? MARKER_CONFIGS[InfrastructureType.GENERIC];
}

/**
 * Obtiene todos los marcadores de un grupo
 */
export function getMarkersByGroup(group: MarkerGroup): MarkerConfig[] {
  return Object.values(MARKER_CONFIGS).filter(config => config.group === group);
}

/**
 * Obtiene el color del marcador para un tipo
 */
export function getMarkerColor(type: InfrastructureType): string {
  return getMarkerConfig(type).color;
}

/**
 * Obtiene el icono (emoji) para un tipo
 */
export function getMarkerIcon(type: InfrastructureType): string {
  return getMarkerConfig(type).icon;
}

/**
 * Genera el texto alternativo accesible para un marcador
 * Combina el tipo con el nombre específico de la infraestructura
 */
export function getMarkerAltText(type: InfrastructureType, name?: string): string {
  const config = getMarkerConfig(type);
  if (name) {
    return `${config.label}: ${name}`;
  }
  return config.ariaLabel;
}

/**
 * Verifica si un color cumple contraste WCAG 3:1 
 * (todos los marcadores están pre-verificados pero útil para validación)
 */
export function meetsContrastRequirement(config: MarkerConfig): boolean {
  // Si usa texto negro, el contraste real es mayor
  if (config.contrastColor === '#000000') {
    return true;
  }
  return config.contrastRatio >= 3.0;
}

/**
 * Obtiene los grupos ordenados para renderizar leyenda
 */
export function getOrderedGroups(): MarkerGroupMeta[] {
  return [...MARKER_GROUPS].sort((a, b) => a.order - b.order);
}

/**
 * Cuenta infraestructuras por grupo (útil para estadísticas)
 */
export function countByGroup(): Record<MarkerGroup, number> {
  const counts: Record<MarkerGroup, number> = {
    emergency: 0,
    health: 0,
    education: 0,
    public: 0,
    infrastructure: 0,
    other: 0,
  };
  
  for (const config of Object.values(MARKER_CONFIGS)) {
    counts[config.group]++;
  }
  
  return counts;
}

/**
 * Lista de todos los tipos de infraestructura disponibles
 */
export function getAllInfrastructureTypes(): InfrastructureType[] {
  return Object.values(InfrastructureType);
}

/**
 * Verifica si un tipo es de emergencia (prioridad visual)
 */
export function isEmergencyType(type: InfrastructureType): boolean {
  return getMarkerConfig(type).group === 'emergency';
}
